// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';
import {
  getPhoneNumber,
  getMerchantPaymentCode,
  usePendingIncomingPayment,
  getSafePaymentTypeCode,
} from '@tectonics/prs/incoming-payment';

import {
  amountToCurrencyString,
} from '@tectonics/prs/money';

import { useCurrentReceipt } from '@tectonics/prs/receipts';
import { getReceiptAmountLocalTotal } from '@tectonics/prs/receipts/receipt';
import { getMoneyFormat } from '../../../commons/money-format';
import { Button } from '../../../atoms';
import airtelIconSrc from './icons/airtel-icon.svg';
import equitelIconSrc from './icons/equitel-icon.svg';
import vodacomIconSrc from './icons/vodacom-icon.svg';
import tigoIconSrc from './icons/tigo-icon.svg';
import mtnIconSrc from './icons/mtn-icon.svg';
import zamtelIconSrc from './icons/zamtel-icon.svg';

export function CellulantPage(): Node {
  const incomingPayment = usePendingIncomingPayment();
  const phoneNumber = getPhoneNumber(incomingPayment);
  const receipt = useCurrentReceipt();
  const decimalFormat = getMoneyFormat(incomingPayment.country);
  const formatAmount = amountToCurrencyString(decimalFormat);
  const formattedAmount = formatAmount(getReceiptAmountLocalTotal(receipt));
  const merchantPaymentCode = getMerchantPaymentCode(incomingPayment);
  const paymentTypeCode = getSafePaymentTypeCode(incomingPayment);

  return (
    <Root>
      <Body>
        You will receive a prompt on your mobile number {phoneNumber} to authorize a payment of {formattedAmount} for {merchantPaymentCode}
      </Body>
      <EWalletWrapper>
        <EWalletIcon src={getEWalletIconSrc(paymentTypeCode)} alt={paymentTypeCode} id={paymentTypeCode} />
        <EWalletName htmlFor={paymentTypeCode}>
          {getEWalletName(paymentTypeCode)}
        </EWalletName>
      </EWalletWrapper>
      <Link as="a" type="primary" flat outline rounded onClick={refreshPage}>
        CONFIRM PAYMENT
      </Link>
    </Root>
  );
}

function refreshPage() {
  window.location.reload();
}

function getEWalletIconSrc(ewallet: string): string {
  switch (ewallet) {
    case 'airtel':
      return airtelIconSrc;
    case 'equitel':
      return equitelIconSrc;
    case 'vodacom':
      return vodacomIconSrc;
    case 'tigo':
      return tigoIconSrc;
    case 'mtn':
      return mtnIconSrc;
    case 'zamtel':
      return zamtelIconSrc;
    default:
      throw new Error(`Ewallet icon not found ${ewallet}`);
  }
}

function getEWalletName(ewallet: string): string {
  switch (ewallet) {
    case 'airtel':
      return 'Airtel';
    case 'equitel':
      return 'Equitel';
    case 'vodacom':
      return 'Vodacom M-PESA';
    case 'tigo':
      return 'Tigo Pesa';
    case 'mtn':
      return 'MTN MoMo';
    case 'zamtel':
      return 'Zamtel';
    default:
      throw new Error(`Ewallet not found ${ewallet}`);
  }
}

const Root = styled.div`
  color: ${props => props.theme.colors.gray(20)};
`;

const Body = styled.div`
  padding: 32px 16px;
  font-size: 1.8em;
  font-family: ${props => props.theme.fontFamily.display};
`;

const EWalletWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const EWalletName = styled.label`
  font-size: 1.8em;
  font-family: ${props => props.theme.fontFamily.display};
  font-weight: 600;
  line-height: 150%;
  color: #212b36;
  padding: 0 0 0 1em;
`;
const EWalletIcon = styled.img``;

const Link = styled(Button)`
  background: #2397d4;
  border: none;
  color: #ffffff;
  margin: 2em auto 2em;
`;
